import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import { AlertBox, FormLabel, FormInput, ModalShell, Button } from '../ui';


export default function AddOrganizationModal({ 
  show, 
  onClose, 
  walletAddress,
  onOrganizationCreated
}) {
  const { t, isDark } = useTheme();
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [checkingHandle, setCheckingHandle] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-generate handle from name
  useEffect(() => {
    if (name.trim()) {
      const generatedHandle = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setHandle(generatedHandle);
    } else {
      setHandle('');
    }
  }, [name]);

  // Check handle availability
  useEffect(() => {
    if (!handle.trim()) {
      setHandleAvailable(null);
      return;
    }

    const checkHandle = async () => {
      setCheckingHandle(true);
      try {
        const { data, error } = await supabase
          .from('entity_handles')
          .select('handle')
          .eq('handle', handle.toLowerCase())
          .single();

        if (error && error.code === 'PGRST116') {
          // No rows returned = handle is available
          setHandleAvailable(true);
        } else if (data) {
          // Handle exists
          setHandleAvailable(false);
        }
      } catch (err) {
        console.error('Error checking handle:', err);
      }
      setCheckingHandle(false);
    };

    const debounce = setTimeout(checkHandle, 500);
    return () => clearTimeout(debounce);
  }, [handle]);

  const resetForm = () => {
    setName('');
    setHandle('');
    setEmail('');
    setWebsite('');
    setLocation('');
    setBio('');
    setHandleAvailable(null);
    setError('');
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!handle.trim()) {
      setError('Handle is required');
      return;
    }
    if (handleAvailable === false) {
      setError('Handle is already taken');
      return;
    }
    if (bio.length > 200) {
      setError('Bio must be 200 characters or less');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const orgId = `org_${Date.now()}`;
      const timestamp = Date.now();

      // Create organization
      const { error: orgError } = await supabase
        .from('organizations')
        .insert({
          id: orgId,
          name: name.trim(),
          bio: bio.trim() || null,
          email: email.trim() || null,
          website: website.trim() || null,
          location: location.trim() || null,
          owner_wallet: walletAddress,
          created_at: timestamp,
          updated_at: timestamp
        });

      if (orgError) throw orgError;

      // Create handle
      const { error: handleError } = await supabase
        .from('entity_handles')
        .insert({
          handle: handle.toLowerCase(),
          entity_type: 'organization',
          entity_id: orgId,
          created_at: timestamp
        });

      if (handleError) throw handleError;

      // Create primary owner seat
      const { error: seatError } = await supabase
        .from('org_seats')
        .insert({
          id: `seat_${Date.now()}`,
          org_id: orgId,
          wallet_address: walletAddress,
          role: 'primary_owner',
          invited_by: null,
          created_at: timestamp
        });

      if (seatError) throw seatError;

      // Success
      onOrganizationCreated();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create org. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!show) return null;

  return (
    <ModalShell onClose={handleClose} maxWidth="500px" blur scrollable panelStyle={{ backgroundColor: t.modalBg }}>
        <h2 style={{ 
          fontFamily: 'Crimson Pro, serif', 
          fontSize: '28px', 
          marginBottom: '8px',
          color: t.text
        }}>
          Create Org
        </h2>
        <p style={{ 
          color: t.textSecondary, 
          marginBottom: '24px',
          fontSize: '14px',
          fontFamily: 'Poppins, sans-serif'
        }}>
          Set up your business or brand profile
        </p>

        {error && (
          <AlertBox>{error}</AlertBox>
        )}

        {/* Name - Required */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Org Name *</FormLabel>
          <FormInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp, Tech Blog, etc."
          />
        </div>

        {/* Handle - Required with availability check */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Handle * <span style={{ color: t.textSecondary, fontWeight: '400' }}>(@{handle || 'yourhandle'})</span></FormLabel>
          <input
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="unique-handle"
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${handleAvailable === false ? t.error : handleAvailable === true ? t.success : t.borderStrong}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: t.surface,
              outline: 'none'
            }}
            onFocus={(e) => {
              if (handleAvailable !== false && handleAvailable !== true) {
                e.target.style.borderColor = t.accent;
              }
            }}
            onBlur={(e) => {
              if (handleAvailable !== false && handleAvailable !== true) {
                e.target.style.borderColor = t.borderStrong;
              }
            }}
          />
          {checkingHandle && (
            <p style={{ fontSize: '12px', color: t.textSecondary, marginTop: '4px', fontFamily: 'Poppins, sans-serif' }}>
              Checking availability...
            </p>
          )}
          {!checkingHandle && handleAvailable === true && (
            <p style={{ fontSize: '12px', color: t.success, marginTop: '4px', fontFamily: 'Poppins, sans-serif' }}>
              ✓ Handle is available
            </p>
          )}
          {!checkingHandle && handleAvailable === false && (
            <p style={{ fontSize: '12px', color: t.error, marginTop: '4px', fontFamily: 'Poppins, sans-serif' }}>
              ✗ Handle is already taken
            </p>
          )}
        </div>

        {/* Email - Optional */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Contact Email</FormLabel>
          <FormInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hello@yourorg.com"
          />
        </div>

        {/* Website - Optional */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Website</FormLabel>
          <FormInput
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://yourwebsite.com"
          />
        </div>

        {/* Location - Optional */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Location</FormLabel>
          <FormInput
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="San Francisco, Remote, Global..."
          />
        </div>

        {/* Bio - Optional with character limit */}
        <div style={{ marginBottom: '24px' }}>
          <FormLabel>Bio <span style={{ color: t.textSecondary, fontWeight: '400' }}>({bio.length}/200)</span></FormLabel>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="What does your org do?"
            rows={3}
            maxLength={200}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${t.borderStrong}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: t.surface,
              outline: 'none',
              resize: 'vertical'
            }}
            onFocus={(e) => e.target.style.borderColor = t.accent}
            onBlur={(e) => e.target.style.borderColor = t.borderStrong}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !handle.trim() || handleAvailable === false || checkingHandle || isSubmitting}
            style={{ flex: 1 }}
          >
            {isSubmitting ? 'Creating...' : 'Create Org'}
          </Button>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting} style={{ flex: 1 }}>
            Cancel
          </Button>
        </div>
    </ModalShell>
  );
}
